import React from 'react';
import { SelectMenu } from '../../../ui-kit';
import { fetchReportDashboard } from '../../../services/manualDashboardService';
import type { ReportDateOption, ReportPayload } from '../../../types/reportDashboard';
import type { ReportQuery } from './useReportCohort';

/**
 * Which date a panel reads the page's range on.
 *
 * The range at the top stays what it is; this only changes the date it is read
 * against for this one panel — "applications sent in March" or "decisions made
 * in March". The report fetches itself again at that date for this panel alone,
 * as the Cohort choice does, and the panel's download follows.
 *
 * A panel only offers the dates its own cases carry: a case still pending has
 * no decision date, and filtering on a date a case does not have would quietly
 * drop it from the list.
 */
export function useReportDates(
  payload: ReportPayload,
  { dashboardId, range, filters, refreshNonce }: ReportQuery,
  options: ReportDateOption[],
  /** What the panel shows when nothing is picked. */
  fallback = 'application',
) {
  const [chosen, setChosen] = React.useState<string | undefined>(undefined);
  React.useEffect(() => setChosen(undefined), [range]);

  const basis = chosen && options.some((o) => o.key === chosen) ? chosen : fallback;
  const active = basis !== fallback;

  const [refetched, setRefetched] = React.useState<ReportPayload | null>(null);
  const [loading, setLoading] = React.useState(false);
  const filtersKey = JSON.stringify(filters);
  React.useEffect(() => {
    if (!active || !dashboardId) {
      setRefetched(null);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    fetchReportDashboard(
      dashboardId,
      { filters: JSON.parse(filtersKey), start: range.start, end: range.end, dateBasis: basis },
      controller.signal,
    )
      .then(setRefetched)
      .catch(() => setRefetched(null))
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [active, dashboardId, basis, range.start, range.end, filtersKey, refreshNonce]);

  // Shown even when a panel has only one date to offer, so the header keeps the
  // same controls in the same places whatever status is being looked at.
  const control = options.length > 0 ? (
    <SelectMenu
      label="Dates"
      value={basis}
      options={options.map((o) => ({ value: o.key, label: o.label, description: o.description }))}
      onChange={setChosen}
    />
  ) : null;

  return {
    /** The report to read this panel from: the page's own, or read on another date. */
    source: active && refetched ? refetched : payload,
    basis,
    control,
    reloading: active && loading,
  };
}
